import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  ShieldBan,
  Trash2,
  Edit2,
  CheckCircle2,
  ShieldCheck,
  Users,
  Box,
  LayoutList,
  Image as ImageIcon,
  Eye,
} from 'lucide-react';
import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';

import type { ClassifiedPackage, ClassifiedProduct, Customer } from '@terra/api-client';
import {
  Badge,
  type BadgeTone,
  Button,
  Card,
  CardBody,
  EmptyState,
  Input,
  PageHeader,
  Spinner,
} from '@terra/ui';

import { api } from '../lib/api';

const formatPaise = (p: number) =>
  `₹${(p / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
function Toggle({
  on,
  onClick,
  disabled,
}: {
  on: boolean;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={on}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors duration-300 disabled:opacity-50 ${on ? 'bg-gold-500' : 'bg-surface-subtle border-border/60 border'}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-300 ${on ? 'translate-x-4' : 'translate-x-0.5'}`}
      />
    </button>
  );
}

// ══════════════════════════════════════════════════════════════════
// CUSTOMER LIST  — "All Customers"
// ══════════════════════════════════════════════════════════════════

export function CustomerListPage() {
  const qc = useQueryClient();
  const [searchDraft, setSearchDraft] = useState('');
  const [verifyDraft, setVerifyDraft] = useState('');
  const [applied, setApplied] = useState<{
    search: string;
    verification: '' | 'VERIFIED' | 'UNVERIFIED';
  }>({ search: '', verification: '' });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkAction, setBulkAction] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);

  const customers = useQuery({
    queryKey: ['customers', applied],
    queryFn: () =>
      api.customers.list({
        ...(applied.search ? { q: applied.search } : {}),
        ...(applied.verification ? { verification: applied.verification } : {}),
      }),
  });
  const packages = useQuery({
    queryKey: ['classified-packages'],
    queryFn: () => api.customers.listPackages(),
  });

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    classifiedPackageId: '',
  });
  const create = useMutation({
    mutationFn: () =>
      api.customers.create({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        password: form.password,
        ...(form.classifiedPackageId ? { classifiedPackageId: form.classifiedPackageId } : {}),
      }),
    onSuccess: () => {
      setForm({ name: '', email: '', phone: '', password: '', classifiedPackageId: '' });
      setShowAdd(false);
      void qc.invalidateQueries({ queryKey: ['customers'] });
    },
  });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Parameters<typeof api.customers.update>[1] }) =>
      api.customers.update(id, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['customers'] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.customers.remove(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['customers'] }),
  });
  const bulkBlock = useMutation({
    mutationFn: async (ids: string[]) => {
      return api.customers.bulkUpdate({ ids, blocked: true });
    },
    onSuccess: (data) => {
      setSelected(new Set());
      setBulkAction('');
      if (data?.failed > 0) alert(`Bulk block partial failure: ${data.failed} failed.`);
      void qc.invalidateQueries({ queryKey: ['customers'] });
    },
  });

  const rows = customers.data ?? [];
  const allChecked = rows.length > 0 && selected.size === rows.length;

  function toggleRow(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="space-y-6 pb-8">
      {/* Legend */}
      <div className="flex items-center gap-4 text-[12px] font-semibold">
        <p className="text-ink-muted flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-red-500 shadow-sm" /> Blocked customer
        </p>
        <p className="text-ink-muted flex items-center gap-2">
          <span className="h-2 w-2 rounded-full shadow-sm" style={{ backgroundColor: '#8B5CF6' }} />{' '}
          Suspicious customer
        </p>
      </div>

      <AnimatePresence>
        {showAdd && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="border-border/60 bg-surface overflow-hidden rounded-2xl border p-6 shadow-sm"
          >
            <h3 className="text-ink-muted mb-4 text-[12px] font-bold uppercase tracking-wider">
              Create New Customer
            </h3>
            <form
              className="grid gap-4 sm:grid-cols-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (form.name && form.email && form.phone && form.password.length >= 8)
                  create.mutate();
              }}
            >
              <Input
                label="Name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                required
              />
              <Input
                label="Email"
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                required
              />
              <Input
                label="Phone"
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                hint="10-digit Indian mobile"
                required
              />
              <Input
                label="Password"
                type="password"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                hint="min 8 chars, mixed case + digit"
                required
              />
              <div>
                <label className="text-ink mb-1 block text-[13px] font-bold">Package</label>
                <select
                  className="border-border/60 bg-surface text-ink focus:border-ink focus:ring-ink/20 h-10 w-full rounded-xl border px-3 text-[13px] font-medium outline-none transition-colors focus:ring-2"
                  value={form.classifiedPackageId}
                  onChange={(e) => setForm((f) => ({ ...f, classifiedPackageId: e.target.value }))}
                >
                  <option value="">No package</option>
                  {(packages.data ?? []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <Button type="submit" loading={create.isPending} className="h-10 w-full">
                  Create customer
                </Button>
              </div>
              {create.isError && (
                <p className="text-[12px] font-semibold text-red-600 sm:col-span-3">
                  Could not create — check the email/phone are unique and valid.
                </p>
              )}
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* List */}
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        {/* Toolbar */}
        <div className="border-border/60 bg-surface-subtle/30 flex flex-col flex-wrap items-center justify-between gap-4 border-b px-5 py-4 sm:flex-row">
          <div className="flex items-center gap-3">
            <div className="bg-surface-subtle text-ink-muted ring-border/60 grid h-8 w-8 place-items-center rounded-lg ring-1 ring-inset">
              <Users className="h-4 w-4" strokeWidth={2} />
            </div>
            <div className="flex items-center gap-2">
              <select
                value={bulkAction}
                onChange={(e) => setBulkAction(e.target.value)}
                className="border-border/60 bg-surface text-ink focus:border-ink focus:ring-ink/20 h-10 rounded-xl border px-3 text-[13px] font-medium outline-none transition-colors focus:ring-2"
              >
                <option value="">Bulk Action</option>
                <option value="BLOCK">Block customers</option>
              </select>
              <button
                type="button"
                disabled={bulkAction !== 'BLOCK' || selected.size === 0 || bulkBlock.isPending}
                onClick={() => {
                  if (window.confirm(`Block ${selected.size} customer(s)?`))
                    bulkBlock.mutate([...selected]);
                }}
                className="border-border/60 bg-surface text-ink hover:bg-surface-subtle flex h-10 items-center justify-center rounded-xl border px-4 text-[13px] font-bold shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:shadow-sm"
              >
                Apply
              </button>
            </div>
          </div>

          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <select
              value={verifyDraft}
              onChange={(e) => setVerifyDraft(e.target.value)}
              className="border-border/60 bg-surface text-ink focus:border-ink focus:ring-ink/20 h-10 w-full rounded-xl border px-3 text-[13px] font-medium outline-none transition-colors focus:ring-2 sm:w-auto"
            >
              <option value="">Filter by Status</option>
              <option value="VERIFIED">Verified</option>
              <option value="UNVERIFIED">Unverified</option>
            </select>
            <div className="relative w-full sm:w-60">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Search className="text-ink-muted h-4 w-4" strokeWidth={2} />
              </div>
              <input
                type="text"
                placeholder="Type email or name & Enter"
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter')
                    setApplied({
                      search: searchDraft.trim(),
                      verification: verifyDraft as '' | 'VERIFIED' | 'UNVERIFIED',
                    });
                }}
                className="border-border/80 bg-surface text-ink focus:border-ink focus:ring-ink/20 placeholder:text-ink-subtle h-10 w-full rounded-xl border pl-9 pr-3 text-[13px] font-medium shadow-sm outline-none transition-colors focus:ring-2"
              />
            </div>
            <button
              onClick={() =>
                setApplied({
                  search: searchDraft.trim(),
                  verification: verifyDraft as '' | 'VERIFIED' | 'UNVERIFIED',
                })
              }
              className="bg-ink hover:bg-ink/90 flex h-10 w-full items-center justify-center rounded-xl px-4 text-[13px] font-bold text-white shadow-sm transition-all sm:w-auto"
            >
              Filter
            </button>
            <div className="bg-border/60 mx-1 hidden h-6 w-px sm:block" />
            <button
              onClick={() => setShowAdd((v) => !v)}
              className="bg-ink hover:bg-ink/90 flex h-10 w-full items-center justify-center rounded-xl px-4 text-[13px] font-bold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md sm:w-auto"
            >
              {showAdd ? 'Close Form' : '+ Add New Customer'}
            </button>
          </div>
        </div>

        <div>
          {customers.isLoading ? (
            <div className="flex flex-col items-center justify-center gap-3 py-12">
              <Spinner size="md" />
              <span className="text-ink-muted text-[13px] font-medium">Loading customers...</span>
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              title="No customers"
              description="Customers will appear here once they register."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-border/60 bg-surface-subtle/30 text-ink-muted border-b text-[11px] font-bold uppercase tracking-wider">
                    <th className="w-12 px-5 py-3.5">
                      <input
                        type="checkbox"
                        checked={allChecked}
                        onChange={() =>
                          setSelected(
                            allChecked ? new Set() : new Set(rows.map((c: Customer) => c.id)),
                          )
                        }
                        className="accent-gold-500 h-4 w-4 cursor-pointer rounded"
                      />
                    </th>
                    <th className="px-5 py-3.5">Name</th>
                    <th className="px-5 py-3.5">Email Address</th>
                    <th className="px-5 py-3.5">Phone</th>
                    <th className="px-5 py-3.5">Package</th>
                    <th className="px-5 py-3.5">Wallet Balance</th>
                    <th className="px-5 py-3.5">Status</th>
                    <th className="px-5 py-3.5 text-right">Options</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c: Customer, idx: number) => {
                    const nameColor = c.blocked
                      ? 'text-red-600'
                      : c.isSuspicious
                        ? 'text-[#8B5CF6]'
                        : 'text-ink';
                    return (
                      <Fragment key={c.id}>
                        <motion.tr
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{
                            delay: (idx % 15) * 0.02,
                            duration: 0.3,
                            ease: [0.16, 1, 0.3, 1],
                          }}
                          className={`border-border/60 hover:bg-surface-subtle/30 border-b transition-colors ${editId === c.id ? 'bg-surface-subtle/50' : ''}`}
                        >
                          <td className="w-12 px-5 py-4">
                            <input
                              type="checkbox"
                              checked={selected.has(c.id)}
                              onChange={() => toggleRow(c.id)}
                              className="accent-gold-500 h-4 w-4 cursor-pointer rounded"
                            />
                          </td>
                          <td className={`px-5 py-4 text-[13px] font-bold ${nameColor}`}>
                            {c.name}
                          </td>
                          <td className="text-ink-muted px-5 py-4 text-[13px] font-medium">
                            {c.email}
                          </td>
                          <td className="text-ink-muted px-5 py-4 text-[13px] font-medium">
                            {c.phone}
                          </td>
                          <td className="text-ink-muted px-5 py-4 text-[13px] font-medium">
                            {c.packageName ?? '—'}
                          </td>
                          <td className="text-ink px-5 py-4 text-[13px] font-bold">
                            {formatPaise(c.walletBalancePaise)}
                          </td>
                          <td className="px-5 py-4">
                            <Badge
                              tone={c.verified ? 'success' : 'neutral'}
                              className="px-1.5 py-0.5 text-[10px] font-bold"
                            >
                              {c.verified ? 'Verified' : 'Unverified'}
                            </Badge>
                          </td>
                          <td className="px-5 py-4">
                            <div className="flex items-center justify-end gap-1">
                              <Link
                                to={`/customers/${c.id}`}
                                className="text-ink-subtle hover:text-gold-600 hover:bg-gold-50 rounded p-1.5 transition-colors"
                                title="View 360 Profile"
                              >
                                <Eye className="h-4 w-4" strokeWidth={2} />
                              </Link>
                              <button
                                type="button"
                                onClick={() => setEditId(editId === c.id ? null : c.id)}
                                className="text-ink-subtle hover:text-ink hover:bg-surface-subtle rounded p-1.5 transition-colors"
                                title="Edit"
                              >
                                <Edit2 className="h-4 w-4" strokeWidth={2} />
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  update.mutate({ id: c.id, body: { blocked: !c.blocked } })
                                }
                                className={`rounded p-1.5 transition-colors ${
                                  c.blocked
                                    ? 'text-gold-500 hover:text-gold-600 hover:bg-gold-50'
                                    : 'text-red-500 hover:bg-red-50 hover:text-red-600'
                                }`}
                                title={c.blocked ? 'Unblock' : 'Block'}
                              >
                                {c.blocked ? (
                                  <CheckCircle2 className="h-4 w-4" strokeWidth={2} />
                                ) : (
                                  <ShieldBan className="h-4 w-4" strokeWidth={2} />
                                )}
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  if (window.confirm(`Delete customer ${c.name}?`))
                                    remove.mutate(c.id);
                                }}
                                className="rounded p-1.5 text-red-500 transition-colors hover:bg-red-50 hover:text-red-600"
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" strokeWidth={2} />
                              </button>
                            </div>
                          </td>
                        </motion.tr>
                        <AnimatePresence>
                          {editId === c.id && (
                            <motion.tr
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="bg-surface-subtle/50"
                            >
                              <td colSpan={8} className="border-border/60 border-b px-5 py-4">
                                <CustomerEditRow
                                  customer={c}
                                  onSave={(body) => {
                                    update.mutate({ id: c.id, body });
                                    setEditId(null);
                                  }}
                                />
                              </td>
                            </motion.tr>
                          )}
                        </AnimatePresence>
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CustomerEditRow({
  customer,
  onSave,
}: {
  customer: Customer;
  onSave: (body: { name: string; phone: string; isSuspicious: boolean }) => void;
}) {
  const [name, setName] = useState(customer.name);
  const [phone, setPhone] = useState(customer.phone);
  const [suspicious, setSuspicious] = useState(customer.isSuspicious);
  return (
    <form
      className="bg-surface border-border/60 flex flex-wrap items-end gap-4 rounded-xl border p-4 shadow-sm"
      onSubmit={(e) => {
        e.preventDefault();
        onSave({ name: name.trim(), phone: phone.trim(), isSuspicious: suspicious });
      }}
    >
      <div className="min-w-[200px] flex-1">
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="min-w-[200px] flex-1">
        <Input label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
      </div>
      <label className="text-ink flex cursor-pointer items-center gap-2 pb-2 text-[13px] font-bold">
        <input
          type="checkbox"
          checked={suspicious}
          onChange={(e) => setSuspicious(e.target.checked)}
          className="accent-gold-500 h-4 w-4 rounded"
        />
        Mark as suspicious
      </label>
      <button
        type="submit"
        className="bg-ink hover:bg-ink/90 flex h-10 items-center justify-center rounded-xl px-6 text-[13px] font-bold text-white shadow-sm transition-all"
      >
        Save Changes
      </button>
    </form>
  );
}

// ══════════════════════════════════════════════════════════════════
// CLASSIFIED PRODUCTS
// ══════════════════════════════════════════════════════════════════

const CLASSIFIED_TONE: Record<string, BadgeTone> = {
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'danger',
};

export function ClassifiedProductsPage() {
  const qc = useQueryClient();
  const products = useQuery({
    queryKey: ['classified-products'],
    queryFn: () => api.customers.listClassifiedProducts(),
  });

  const update = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Parameters<typeof api.customers.updateClassifiedProduct>[1];
    }) => api.customers.updateClassifiedProduct(id, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['classified-products'] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.customers.removeClassifiedProduct(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['classified-products'] }),
  });

  const rows = products.data ?? [];

  return (
    <div className="space-y-6 pb-8">
      {/* Topbar renders the title, no local header needed here */}

      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <div className="border-border/60 bg-surface-subtle/30 flex items-center gap-3 border-b px-5 py-4">
          <div className="bg-surface-subtle text-ink-muted ring-border/60 grid h-8 w-8 place-items-center rounded-lg ring-1 ring-inset">
            <Box className="h-4 w-4" strokeWidth={2} />
          </div>
          <h2 className="text-ink-muted text-[13px] font-bold uppercase tracking-wider">
            All Listings
          </h2>
        </div>

        <div>
          {products.isLoading ? (
            <div className="flex flex-col items-center justify-center gap-3 py-12">
              <Spinner size="md" />
              <span className="text-ink-muted text-[13px] font-medium">Loading listings...</span>
            </div>
          ) : rows.length === 0 ? (
            <div className="grid place-items-center py-16 text-center">
              <div className="bg-surface-subtle text-ink-muted grid h-16 w-16 place-items-center rounded-full">
                <Box className="h-6 w-6 opacity-50" />
              </div>
              <p className="text-ink mt-4 text-[14px] font-bold">No classified products found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-border/60 bg-surface-subtle/30 text-ink-muted border-b text-[11px] font-bold uppercase tracking-wider">
                    <th className="w-12 px-5 py-3.5">#</th>
                    <th className="px-5 py-3.5">Image</th>
                    <th className="px-5 py-3.5">Name</th>
                    <th className="px-5 py-3.5">Uploaded By</th>
                    <th className="px-5 py-3.5">Status</th>
                    <th className="px-5 py-3.5">Published</th>
                    <th className="px-5 py-3.5 text-right">Options</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((p: ClassifiedProduct, idx: number) => (
                    <motion.tr
                      key={p.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        delay: (idx % 15) * 0.02,
                        duration: 0.3,
                        ease: [0.16, 1, 0.3, 1],
                      }}
                      className="border-border/60 hover:bg-surface-subtle/30 border-b transition-colors"
                    >
                      <td className="text-ink-muted px-5 py-4 text-[13px] font-bold">{idx + 1}</td>
                      <td className="px-5 py-4">
                        {p.imageUrl ? (
                          <img
                            src={p.imageUrl}
                            alt={p.name}
                            className="border-border/60 h-10 w-10 rounded-lg border object-cover"
                          />
                        ) : (
                          <div className="bg-surface-subtle text-ink-subtle border-border/60 grid h-10 w-10 place-items-center rounded-lg border">
                            <ImageIcon className="h-4 w-4" />
                          </div>
                        )}
                      </td>
                      <td className="text-ink px-5 py-4 text-[13px] font-bold">{p.name}</td>
                      <td className="text-ink-muted px-5 py-4 text-[13px] font-medium">
                        {p.uploadedByName ?? '—'}
                      </td>
                      <td className="px-5 py-4">
                        <Badge
                          tone={CLASSIFIED_TONE[p.status] ?? 'neutral'}
                          className="px-1.5 py-0.5 text-[10px] font-bold"
                        >
                          {p.status}
                        </Badge>
                      </td>
                      <td className="px-5 py-4">
                        <Toggle
                          on={p.published}
                          disabled={update.isPending}
                          onClick={() =>
                            update.mutate({ id: p.id, body: { published: !p.published } })
                          }
                        />
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center justify-end gap-1">
                          {p.status !== 'APPROVED' && (
                            <button
                              type="button"
                              onClick={() =>
                                update.mutate({ id: p.id, body: { status: 'APPROVED' } })
                              }
                              className="text-gold-500 hover:text-gold-600 hover:bg-gold-50 rounded p-1.5 transition-colors"
                              title="Approve"
                            >
                              <ShieldCheck className="h-4 w-4" strokeWidth={2} />
                            </button>
                          )}
                          {p.status !== 'REJECTED' && (
                            <button
                              type="button"
                              onClick={() =>
                                update.mutate({ id: p.id, body: { status: 'REJECTED' } })
                              }
                              className="rounded p-1.5 text-orange-500 transition-colors hover:bg-orange-50 hover:text-orange-600"
                              title="Reject"
                            >
                              <ShieldBan className="h-4 w-4" strokeWidth={2} />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              if (window.confirm('Delete this classified product?'))
                                remove.mutate(p.id);
                            }}
                            className="rounded p-1.5 text-red-500 transition-colors hover:bg-red-50 hover:text-red-600"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" strokeWidth={2} />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// CLASSIFIED PACKAGES
// ══════════════════════════════════════════════════════════════════

export function ClassifiedPackagesPage() {
  const qc = useQueryClient();
  const packages = useQuery({
    queryKey: ['classified-packages'],
    queryFn: () => api.customers.listPackages(),
  });
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    priceRupees: '',
    durationDays: '30',
    adLimit: '10',
  });

  const create = useMutation({
    mutationFn: () =>
      api.customers.createPackage({
        name: form.name.trim(),
        ...(form.description ? { description: form.description } : {}),
        pricePaise: Math.round(Number(form.priceRupees) * 100),
        durationDays: Number(form.durationDays),
        adLimit: Number(form.adLimit),
      }),
    onSuccess: () => {
      setForm({ name: '', description: '', priceRupees: '', durationDays: '30', adLimit: '10' });
      setShowAdd(false);
      void qc.invalidateQueries({ queryKey: ['classified-packages'] });
    },
  });
  const toggle = useMutation({
    mutationFn: (p: ClassifiedPackage) =>
      api.customers.updatePackage(p.id, {
        name: p.name,
        pricePaise: p.pricePaise,
        durationDays: p.durationDays,
        adLimit: p.adLimit,
        active: !p.active,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['classified-packages'] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.customers.removePackage(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['classified-packages'] }),
  });

  const rows = packages.data ?? [];

  return (
    <div className="space-y-6 pb-8">
      <AnimatePresence>
        {showAdd && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="border-border/60 bg-surface overflow-hidden rounded-2xl border p-6 shadow-sm"
          >
            <h3 className="text-ink-muted mb-4 text-[12px] font-bold uppercase tracking-wider">
              Create New Package
            </h3>
            <form
              className="grid gap-4 sm:grid-cols-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (form.name.length >= 2 && Number(form.priceRupees) >= 0) create.mutate();
              }}
            >
              <Input
                label="Name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                required
              />
              <Input
                label="Price (₹)"
                type="number"
                min="0"
                step="0.01"
                value={form.priceRupees}
                onChange={(e) => setForm((f) => ({ ...f, priceRupees: e.target.value }))}
                required
              />
              <Input
                label="Duration (days)"
                type="number"
                min="1"
                value={form.durationDays}
                onChange={(e) => setForm((f) => ({ ...f, durationDays: e.target.value }))}
              />
              <Input
                label="Ad limit"
                type="number"
                min="1"
                value={form.adLimit}
                onChange={(e) => setForm((f) => ({ ...f, adLimit: e.target.value }))}
              />
              <Input
                label="Description"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
              <div className="flex items-end">
                <Button type="submit" loading={create.isPending} className="h-10 w-full">
                  Create package
                </Button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <div className="border-border/60 bg-surface-subtle/30 flex items-center justify-between gap-3 border-b px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="bg-surface-subtle text-ink-muted ring-border/60 grid h-8 w-8 place-items-center rounded-lg ring-1 ring-inset">
              <LayoutList className="h-4 w-4" strokeWidth={2} />
            </div>
            <h2 className="text-ink-muted text-[13px] font-bold uppercase tracking-wider">
              All Packages
            </h2>
          </div>
          <button
            onClick={() => setShowAdd((v) => !v)}
            className="bg-ink hover:bg-ink/90 flex h-9 items-center justify-center rounded-xl px-4 text-[13px] font-bold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
          >
            {showAdd ? 'Close Form' : '+ Add New Package'}
          </button>
        </div>

        <div>
          {packages.isLoading ? (
            <div className="flex flex-col items-center justify-center gap-3 py-12">
              <Spinner size="md" />
              <span className="text-ink-muted text-[13px] font-medium">Loading packages...</span>
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              title="No classified packages"
              description="Create your first package above."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-border/60 bg-surface-subtle/30 text-ink-muted border-b text-[11px] font-bold uppercase tracking-wider">
                    <th className="px-5 py-3.5">Name</th>
                    <th className="px-5 py-3.5">Price</th>
                    <th className="px-5 py-3.5">Duration</th>
                    <th className="px-5 py-3.5">Ad Limit</th>
                    <th className="px-5 py-3.5">Active</th>
                    <th className="px-5 py-3.5 text-right">Options</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((p: ClassifiedPackage, idx: number) => (
                    <motion.tr
                      key={p.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        delay: (idx % 15) * 0.02,
                        duration: 0.3,
                        ease: [0.16, 1, 0.3, 1],
                      }}
                      className="border-border/60 hover:bg-surface-subtle/30 border-b transition-colors"
                    >
                      <td className="text-ink px-5 py-4 text-[13px] font-bold">{p.name}</td>
                      <td className="text-ink px-5 py-4 text-[13px] font-bold">
                        {formatPaise(p.pricePaise)}
                      </td>
                      <td className="text-ink-muted px-5 py-4 text-[13px] font-medium">
                        {p.durationDays} days
                      </td>
                      <td className="text-ink-muted px-5 py-4 text-[13px] font-medium">
                        {p.adLimit} ads
                      </td>
                      <td className="px-5 py-4">
                        <Toggle
                          on={p.active}
                          disabled={toggle.isPending}
                          onClick={() => toggle.mutate(p)}
                        />
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center justify-end">
                          <button
                            type="button"
                            onClick={() => {
                              if (window.confirm(`Delete package ${p.name}?`)) remove.mutate(p.id);
                            }}
                            className="rounded p-1.5 text-red-500 transition-colors hover:bg-red-50 hover:text-red-600"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" strokeWidth={2} />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
